/**
 * Normalizes error payloads coming off the API into plain strings.
 *
 * FastAPI's `detail` is a string for HTTPException, but a LIST of validation
 * objects ({type, loc, msg, input, ctx}) for a 422. Handing that list straight
 * to React crashes the tree ("Objects are not valid as a React child"), so
 * every read of `response.data.detail` must go through here.
 */

type ValidationItem = {
  loc?: unknown;
  msg?: unknown;
  type?: unknown;
  message?: unknown;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

// ["body", "description"] -> "description"; ["body", "items", 0, "title"] -> "items.0.title"
const formatLocation = (loc: unknown): string => {
  if (!Array.isArray(loc)) return '';
  return loc
    .filter((part) => part !== 'body' && part !== 'query' && part !== 'path')
    .map((part) => String(part))
    .join('.');
};

const formatValidationItem = (item: ValidationItem): string | undefined => {
  const msg =
    typeof item.msg === 'string'
      ? item.msg
      : typeof item.message === 'string'
        ? item.message
        : typeof item.type === 'string'
          ? item.type
          : undefined;
  if (!msg) return undefined;

  const field = formatLocation(item.loc);
  return field ? `${field}: ${msg}` : msg;
};

/**
 * Turns any shape of FastAPI `detail` into a displayable string.
 * Returns undefined when there is nothing meaningful to show, so callers can
 * fall back to their own message.
 */
export const normalizeErrorDetail = (detail: unknown): string | undefined => {
  if (typeof detail === 'string') {
    return detail.trim() || undefined;
  }

  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        if (typeof item === 'string') return item.trim() || undefined;
        if (isRecord(item)) return formatValidationItem(item);
        return undefined;
      })
      .filter((part): part is string => !!part);
    return parts.length ? parts.join('; ') : undefined;
  }

  if (isRecord(detail)) {
    return formatValidationItem(detail);
  }

  return undefined;
};
